<template>
  <div>
    <!-- Schema.org structured data for SEO -->
    <div itemscope itemtype="https://schema.org/LocalBusiness">
      <meta itemprop="name" content="Premium Quality Drywall"/>
      <meta itemprop="url" content="https://www.premiumqualitydrywall.com/"/>
      <meta itemprop="telephone" content="(818) 429-2924"/>
      <meta itemprop="priceRange" content="$$"/>
      <div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
        <meta itemprop="addressLocality" content="San Fernando Valley"/>
        <meta itemprop="addressRegion" content="CA"/>
      </div>
    </div>

    <!-- Hero Section -->
    <v-sheet
      color="primary"
      class="hero-section"
      elevation="4"
    >
      <v-container>
        <v-row align="center" justify="center" class="py-12">
          <v-col cols="12" md="8" class="text-center">
            <h1 class="hero-title white--text mb-6">
              <span class="gradient-text">Premium Quality</span> Drywall
            </h1>
            <p class="text-h5 white--text mb-8 font-weight-light">
              Professional Drywall Services in San Fernando Valley
            </p>
            <p class="text-h6 white--text mb-10 font-weight-light">
              Over 30 years of excellence • Licensed & Bonded • Family Owned
            </p>
            <v-btn
              x-large
              color="accent"
              elevation="8"
              :to="{name: 'contact'}"
              class="hero-cta"
            >
              <v-icon left>mdi-phone</v-icon>
              Get Free Estimate
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>

    <!-- Logo Display -->
    <v-container>
      <v-row justify="center" class="my-12">
        <v-col cols="10" sm="8" md="6" lg="5">
          <v-card elevation="8" class="pa-4" rounded="lg">
            <v-img
              :src="getAssetImg('/assets/logo-color-crop_wcl7z9.webp')"
              class="logo-img"
            ></v-img>
          </v-card>
        </v-col>
      </v-row>

      <!-- Features Section -->
      <v-row class="mb-12">
        <v-col cols="12" class="text-center mb-8">
          <h2 class="text-h3 font-weight-bold primary--text mb-4">
            Why Choose Us?
          </h2>
          <p class="text-h6 grey--text text--darken-1">
            Quality craftsmanship, competitive pricing, and unmatched customer service
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="feature in features"
          :key="feature.title"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card
            elevation="6"
            class="feature-card pa-6 text-center"
            height="100%"
            hover
          >
            <v-avatar
              size="80"
              color="primary"
              class="mb-4 feature-icon"
            >
              <v-icon size="48" color="white">{{ feature.icon }}</v-icon>
            </v-avatar>
            <h3 class="text-h5 font-weight-bold mb-3">{{ feature.title }}</h3>
            <p class="text-body-1 grey--text text--darken-1">
              {{ feature.description }}
            </p>
          </v-card>
        </v-col>
      </v-row>

      <!-- Service Areas -->
      <v-row class="mt-12 mb-8">
        <v-col cols="12">
          <v-card
            elevation="8"
            class="pa-8 text-center service-area-card"
            color="secondary"
            dark
          >
            <h3 class="text-h4 font-weight-bold mb-4">
              Serving the Entire San Fernando Valley
            </h3>
            <p class="text-h6 font-weight-light">
              From small repairs to large-scale projects, we handle it all with expertise and care.
              No job is too big or too small for our experienced team.
            </p>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>

export default {
  components: {},
  name: "HomeView",
  metaInfo: {
    title: 'Home',
    link: [{rel: 'canonical', href: 'https://www.premiumqualitydrywall.com'}]
  },
  data() {
    return {
      features: [
        {
          icon: 'mdi-certificate',
          title: 'Licensed & Bonded',
          description: 'Fully licensed contractors with comprehensive insurance coverage for your peace of mind.'
        },
        {
          icon: 'mdi-account-group',
          title: 'Family Owned',
          description: 'Small family business with big attributes. We treat every customer like family.'
        },
        {
          icon: 'mdi-star',
          title: '30+ Years Experience',
          description: 'Decades of expertise ensuring quality workmanship on every project, big or small.'
        },
        {
          icon: 'mdi-currency-usd-off',
          title: 'Competitive Pricing',
          description: 'Exceptional quality at fair prices. Get more value for your investment.'
        },
        {
          icon: 'mdi-hammer-wrench',
          title: 'Full Service',
          description: 'From metal framing to finishing touches, we handle all your drywall needs.'
        },
        {
          icon: 'mdi-thumb-up',
          title: 'Customer Satisfaction',
          description: 'Long-lasting relationships built on quality work and reliable service.'
        }
      ]
    }
  }
};
</script>

<style scoped>
.hero-section {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  position: relative;
  overflow: hidden;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.gradient-text {
  background: linear-gradient(45deg, #FFF 0%, #8abcd7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-cta {
  font-size: 1.2rem;
  font-weight: 600;
  padding: 28px 48px !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.hero-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 188, 212, 0.6) !important;
}

.feature-card {
  transition: all 0.3s ease;
  border-radius: 12px;
}

.feature-card:hover {
  transform: translateY(-8px);
}

.feature-icon {
  transition: all 0.3s ease;
}

.feature-card:hover .feature-icon {
  transform: scale(1.1);
}

.logo-img {
  border-radius: 8px;
}

.service-area-card {
  border-radius: 12px;
  background: linear-gradient(135deg, #6a6a6a 0%, #979696 100%) !important;
}

@media (max-width: 960px) {
  .hero-title {
    font-size: 2.5rem;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-cta {
    font-size: 1rem;
    padding: 20px 32px !important;
  }
}
</style>
